import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUpload, FiFile, FiX, FiAlertCircle, FiCheckCircle, FiDownload, FiCode } from 'react-icons/fi';
import { extractNpdSpecs, generateNpdDxf } from '../../utils/npdApi';

const formatSize = (b) => (b > 1024 ? `${(b / 1024).toFixed(1)} KB` : `${b} B`);

const toTitle = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const field = (obj, key) => {
  const value = obj?.[key];
  if (value === undefined || value === null || value === '') return '-';
  return String(value);
};

const NPDUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loadingAction, setLoadingAction] = useState('');
  const [error, setError] = useState('');
  const [specs, setSpecs] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [downloadMeta, setDownloadMeta] = useState(null);
  const fileInputRef = useRef();

  const addFile = (incoming) => {
    const file = Array.from(incoming || [])[0];
    if (!file) return;
    if (!/\.(pdf|jpg|jpeg|png|dxf)$/i.test(file.name)) {
      setError('Please upload a PDF, JPG, PNG, or DXF file.');
      return;
    }
    setError('');
    setSpecs(null);
    setShowRaw(false);
    setDownloadMeta(null);
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFile(e.dataTransfer.files);
  };

  const handleExtract = async () => {
    if (!selectedFile) return;
    setLoadingAction('extract');
    setError('');
    setDownloadMeta(null);
    try {
      const data = await extractNpdSpecs(selectedFile);
      setSpecs(data);
      setShowRaw(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction('');
    }
  };

  const handleGenerate = async () => {
    if (!selectedFile) return;
    setLoadingAction('generate');
    setError('');
    try {
      const { blob, filename } = await generateNpdDxf(selectedFile);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'generated.dxf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDownloadMeta({ filename: filename || 'generated.dxf', size: blob.size });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction('');
    }
  };

  return (
    <div className="npd-upload-page">
      <motion.div
        className="npd-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="npd-card-header">
          <div className="npd-card-title">
            <FiUpload size={16} />
            <span>Upload Drawing (PDF, JPG, PNG, or DXF)</span>
          </div>
        </div>

        <motion.div
          className={`npd-dropzone ${dragOver ? 'drag-over' : ''} ${selectedFile ? 'has-file' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current.click()}
          animate={{ scale: dragOver ? 1.02 : 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 22 }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.dxf,application/pdf,image/jpeg,image/png"
            style={{ display: 'none' }}
            onChange={(e) => addFile(e.target.files)}
          />

          <AnimatePresence mode="wait">
            {selectedFile ? (
              <motion.div
                key="selected"
                className="npd-file-selected"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <div className="npd-file-icon-wrap"><FiFile size={20} /></div>
                <div>
                  <div className="npd-file-name">{selectedFile.name}</div>
                  <div className="npd-file-size">{formatSize(selectedFile.size)}</div>
                </div>
                <button
                  className="npd-remove-file"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setSpecs(null);
                    setShowRaw(false);
                    setDownloadMeta(null);
                    setError('');
                  }}
                  title="Remove"
                >
                  <FiX size={12} />
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
              >
                <div className="npd-drop-icon-wrap"><FiUpload size={26} color="#fff" /></div>
                <p className="npd-drop-text">Drag drawing files here</p>
                <p className="npd-drop-subtext">or click to browse (PDF, JPG, PNG, DXF)</p>
                <p className="npd-drop-type">Supported types: <strong>.pdf .jpg .png .dxf</strong></p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence>
          {error && (
            <motion.div
              key="err"
              className="npd-feedback error"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <FiAlertCircle size={14} />
              <span>{error}</span>
            </motion.div>
          )}
          {downloadMeta && (
            <motion.div
              key="dl"
              className="npd-feedback success"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <FiCheckCircle size={14} />
              <span>DXF downloaded: <strong>{downloadMeta.filename}</strong> ({formatSize(downloadMeta.size)})</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="npd-card-footer" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <motion.button
            className="npd-btn secondary"
            onClick={handleExtract}
            disabled={!selectedFile || Boolean(loadingAction)}
            whileTap={{ scale: 0.96 }}
          >
            {loadingAction === 'extract' ? (<><div className="npd-btn-spinner" /> Extracting...</>) : (<><FiCode size={14} /> Extract Specs</>)}
          </motion.button>

          <motion.button
            className="npd-btn primary"
            onClick={handleGenerate}
            disabled={!selectedFile || Boolean(loadingAction)}
            whileTap={{ scale: 0.96 }}
          >
            {loadingAction === 'generate' ? (<><div className="npd-btn-spinner" /> Generating...</>) : (<><FiDownload size={14} /> Generate DXF</>)}
          </motion.button>
        </div>
      </motion.div>

      {specs && (
        <motion.div
          className="npd-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08, duration: 0.3 }}
        >
          <div className="npd-card-header">
            <div className="npd-card-title">
              <FiCode size={16} />
              <span>Extracted Specs</span>
            </div>
            <button className="npd-link-btn" onClick={() => setShowRaw((p) => !p)}>
              {showRaw ? 'Hide Raw JSON' : 'Show Raw JSON'}
            </button>
          </div>

          <div className="npd-spec-sheet">
            <div className="npd-spec-grid">
              <div className="npd-spec-item"><span className="npd-spec-label">Part Number</span><span className="npd-spec-value">{field(specs?.specs || specs, 'part_number')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Description</span><span className="npd-spec-value">{field(specs?.specs || specs, 'description')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Nominal Size</span><span className="npd-spec-value">{field(specs?.specs || specs, 'nominal_size')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Pitch (mm)</span><span className="npd-spec-value">{field(specs?.specs || specs, 'pitch_mm')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Length (mm)</span><span className="npd-spec-value">{field(specs?.specs || specs, 'length_mm')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Thread Standard</span><span className="npd-spec-value">{field(specs?.specs || specs, 'thread_standard')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Thread Direction</span><span className="npd-spec-value">{field(specs?.specs || specs, 'thread_direction')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Head Type</span><span className="npd-spec-value">{field(specs?.specs || specs, 'head_type')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">End Type</span><span className="npd-spec-value">{field(specs?.specs || specs, 'end_type')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Material</span><span className="npd-spec-value">{field(specs?.specs || specs, 'material')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Mechanical Class</span><span className="npd-spec-value">{field(specs?.specs || specs, 'mechanical_property_class')}</span></div>
              <div className="npd-spec-item"><span className="npd-spec-label">Surface Finish</span><span className="npd-spec-value">{field(specs?.specs || specs, 'surface_finish')}</span></div>
            </div>

            <div className="npd-spec-section">
              <h4>Dimensional Details</h4>
              <div className="npd-spec-grid">
                {Object.entries(specs?.specs || specs)
                  .filter(([k, v]) => k !== 'general_notes' && !['part_number', 'description', 'nominal_size', 'pitch_mm', 'length_mm', 'thread_standard', 'thread_direction', 'head_type', 'end_type', 'material', 'mechanical_property_class', 'surface_finish'].includes(k) && typeof v !== 'object')
                  .map(([k, v]) => (
                    <div className="npd-spec-item" key={k}>
                      <span className="npd-spec-label">{toTitle(k)}</span>
                      <span className="npd-spec-value">{String(v)}</span>
                    </div>
                  ))}
              </div>
            </div>

            {Array.isArray((specs?.specs || specs)?.general_notes) && (specs?.specs || specs).general_notes.length > 0 && (
              <div className="npd-spec-section">
                <h4>General Notes</h4>
                <ul className="npd-spec-notes">
                  {(specs?.specs || specs).general_notes.map((n, idx) => (
                    <li key={idx}>{n}</li>
                  ))}
                </ul>
              </div>
            )}

            {showRaw && (
              <div className="npd-spec-section">
                <h4>Raw JSON</h4>
                <div className="npd-table-wrap" style={{ padding: 0 }}>
                  <pre className="npd-mono" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {JSON.stringify(specs, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default NPDUpload;
